import { useCallback, useEffect, useRef, useState } from 'react';

import { CLIENT_ID, getAccessToken } from '@/utils/http';
import { Item } from '@/features/backlog/types';
import { Camera, CanvasElement, RemoteCamera, RemoteCursor, RemoteLock, WhiteboardComment } from './types';

/** The realtime envelope (mirrors backend `defs.Event`). */
interface RealtimeEvent {
  type: string;
  board_id: string;
  actor_id?: string | null;
  client_id?: string | null;
  payload?: unknown;
}

/** ws(s):// URL for a whiteboard room. The whiteboard id rides as `board_id` (the hub's room key,
 *  whatever it names it); `client_id` lets the hub stamp cursor echoes so this tab can skip its own. */
const wsUrl = (whiteboardId: string, actorId: string, accessToken: string | null): string => {
  const base = window.config.apiUrl.replace(/^http/, 'ws');
  const u = new URL(`${base}/ws`);
  u.searchParams.set('board_id', whiteboardId);
  u.searchParams.set('client_id', CLIENT_ID);
  if (actorId) u.searchParams.set('actor_id', actorId);
  const t = accessToken;
  if (t) u.searchParams.set('access_token', t);
  return u.toString();
};

const CURSOR_SEND_INTERVAL = 40; // ms — client-side throttle (~25Hz); the hub also throttles
const CURSOR_TTL = 6000; // ms — drop a remote cursor we haven't heard from in this long
const CAMERA_SEND_INTERVAL = 45; // ms — client-side throttle for camera (follow) frames (~22Hz)
const CAMERA_TTL = 12000; // ms — drop a peer camera we haven't heard from in this long
const SELECTION_HEARTBEAT = 4000; // ms — re-broadcast our selection (and last camera) so a drop self-heals
const SELECTION_TTL = 12000; // ms — release a peer's locks if we haven't heard from them (crash fallback)

interface Handlers {
  self: { id: string; name: string; color: string };
  onUpsert: (el: CanvasElement) => void;
  onDelete: (ids: string[]) => void;
  /** A peer created/edited/resolved/moved a comment (echo of our own is dropped by client id). */
  onCommentUpsert?: (c: WhiteboardComment) => void;
  /** A peer deleted a comment thread — the removed ids (root + replies). */
  onCommentDelete?: (ids: string[]) => void;
  /** A backlog ITEM referenced by a card here changed / was deleted — fanned in from its board room,
   *  so an edit made anywhere (kanban, table, drawer, another whiteboard) updates the card live. */
  onItemUpsert?: (item: Item) => void;
  onItemDelete?: (ids: string[]) => void;
  /** A peer asked us (target = our member id, or "all") to jump to and follow their viewport. */
  onBrought?: (from: { memberId: string; name: string; color: string }, cam: Camera) => void;
  onReconnect?: () => void;
}

/**
 * Holds one WebSocket to a whiteboard room and turns it into (a) presence, (b) an element-event
 * stream applied via onUpsert/onDelete (skipping THIS tab's own echoes), and (c) bidirectional live
 * cursors. Deliberately independent of the board-scoped RealtimeProvider so the canvas page stands
 * alone. Reconnects with backoff; on a genuine reconnect it calls onReconnect so the caller refetches.
 */
export function useWhiteboardRealtime(whiteboardId: string | null, h: Handlers) {
  const [presence, setPresence] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const [cursors, setCursors] = useState<RemoteCursor[]>([]);
  const [locks, setLocks] = useState<Map<string, RemoteLock>>(new Map());

  // Handlers/self in a ref so the socket effect doesn't churn when they change identity.
  const hRef = useRef(h);
  hRef.current = h;

  const wsRef = useRef<WebSocket | null>(null);
  const cursorMap = useRef<Map<string, RemoteCursor>>(new Map());
  const lastSent = useRef(0);
  // Peer cameras keyed by member id (last tab wins) for follow — kept in a ref (not state) so the
  // 22Hz stream never re-renders; the follow loop reads getPeerCamera() each frame. `lastCamera` is OUR
  // camera, re-broadcast on the heartbeat so an idle presenter's followers don't age out.
  const cameraMap = useRef<Map<string, RemoteCamera>>(new Map());
  const lastCamSent = useRef(0);
  const lastCamera = useRef<Camera | null>(null);
  // While WE are following someone, our own camera is a passenger view — don't heartbeat it (it would be
  // stale relative to what we're actually showing and mislead anyone following us).
  const suppressCamera = useRef(false);
  // Peer selections keyed by their client id (→ which elements each locks). `lastSelection` is OUR current
  // selection, re-sent on the heartbeat + on (re)connect so peers' locks stay in sync.
  const selMap = useRef<Map<string, { memberId: string; name: string; color: string; selected: string[]; editing: string | null; ts: number }>>(new Map());
  const lastSelection = useRef<{ selected: string[]; editing: string | null }>({ selected: [], editing: null });
  const rebuildLocks = useCallback(() => {
    const m = new Map<string, RemoteLock>();
    selMap.current.forEach((s) => {
      for (const id of s.selected) {
        const editing = s.editing === id;
        const cur = m.get(id);
        if (!cur || (editing && !cur.editing)) m.set(id, { memberId: s.memberId, name: s.name, color: s.color, editing });
      }
    });
    setLocks(m);
  }, []);

  useEffect(() => {
    if (!whiteboardId) {
      setPresence([]);
      setConnected(false);
      return undefined;
    }
    let ws: WebSocket | null = null;
    let closedByUs = false;
    let didConnect = false;
    let retry = 0;
    let timer: number | undefined;

    const connect = () => {
      ws = new WebSocket(wsUrl(whiteboardId, hRef.current.self.id, getAccessToken()));
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        if (didConnect) hRef.current.onReconnect?.();
        didConnect = true;
        // Re-assert our current selection so peers (re)establish our locks after a (re)connect.
        const s = hRef.current.self;
        const sel = lastSelection.current;
        wsRef.current?.send(JSON.stringify({ type: 'selection.sync', name: s.name, color: s.color, selected: sel.selected, editing: sel.editing }));
      };
      ws.onmessage = (e) => {
        let ev: RealtimeEvent;
        try {
          ev = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (ev.type === 'presence.sync') {
          setPresence((ev.payload as { members?: string[] })?.members ?? []);
          return;
        }
        // Our own echoes are already applied locally (optimistic) — skip by originating tab id.
        if (ev.client_id && ev.client_id === CLIENT_ID) return;
        if (ev.type === 'whiteboard_element.upsert') {
          const el = ev.payload as CanvasElement;
          if (el && el.id) hRef.current.onUpsert(el);
        } else if (ev.type === 'whiteboard_element.delete') {
          const ids = ((ev.payload as { ids?: string[] })?.ids ?? []) as string[];
          if (ids.length) hRef.current.onDelete(ids);
        } else if (ev.type === 'whiteboard_comment.upsert') {
          const c = ev.payload as WhiteboardComment;
          if (c && c.id) hRef.current.onCommentUpsert?.(c);
        } else if (ev.type === 'whiteboard_comment.delete') {
          const ids = ((ev.payload as { ids?: string[] })?.ids ?? []) as string[];
          if (ids.length) hRef.current.onCommentDelete?.(ids);
        } else if (ev.type === 'item.upsert') {
          const it = ev.payload as Item;
          if (it && it.id) hRef.current.onItemUpsert?.(it);
        } else if (ev.type === 'item.delete') {
          const ids = ((ev.payload as { ids?: string[] })?.ids ?? []) as string[];
          if (ids.length) hRef.current.onItemDelete?.(ids);
        } else if (ev.type === 'cursor.sync') {
          const p = ev.payload as { member_id?: string; name?: string; color?: string; x?: number | null; y?: number | null };
          const key = ev.client_id || p.member_id || '';
          if (!key) return;
          if (p.x == null || p.y == null) {
            cursorMap.current.delete(key);
          } else {
            cursorMap.current.set(key, {
              clientId: key,
              memberId: p.member_id ?? '',
              name: p.name ?? '',
              color: p.color || '#888',
              x: p.x,
              y: p.y,
              ts: Date.now(),
            });
          }
          setCursors(Array.from(cursorMap.current.values()));
        } else if (ev.type === 'selection.sync') {
          const p = ev.payload as { member_id?: string; name?: string; color?: string; selected?: string[]; editing?: string | null };
          const key = ev.client_id || p.member_id || '';
          if (!key) return;
          const selected = p.selected ?? [];
          if (selected.length === 0) selMap.current.delete(key); // empty (deselect / disconnect) → release locks
          else selMap.current.set(key, { memberId: p.member_id ?? '', name: p.name ?? '', color: p.color || '#888', selected, editing: p.editing ?? null, ts: Date.now() });
          rebuildLocks();
        } else if (ev.type === 'camera.sync') {
          const p = ev.payload as { member_id?: string; name?: string; color?: string; x?: number | null; y?: number | null; zoom?: number | null };
          if (!p.member_id || p.x == null || p.y == null || p.zoom == null) return;
          cameraMap.current.set(p.member_id, { memberId: p.member_id, name: p.name ?? '', color: p.color || '#888', x: p.x, y: p.y, zoom: p.zoom, ts: Date.now() });
        } else if (ev.type === 'bring.here') {
          const p = ev.payload as { member_id?: string; name?: string; color?: string; x?: number | null; y?: number | null; zoom?: number | null; target?: string };
          const target = p.target ?? '';
          if (!p.member_id || p.x == null || p.y == null || p.zoom == null) return;
          if (target !== 'all' && target !== hRef.current.self.id) return; // not addressed to us
          hRef.current.onBrought?.({ memberId: p.member_id, name: p.name ?? '', color: p.color || '#888' }, { x: p.x, y: p.y, zoom: p.zoom });
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedByUs) return;
        retry += 1;
        const delay = Math.min(1000 * 2 ** Math.min(retry, 4), 15000);
        timer = window.setTimeout(connect, delay);
      };
      ws.onerror = () => ws?.close();
    };
    connect();

    // Age out cursors + selection locks from tabs that went quiet without a clean "left" signal.
    const prune = window.setInterval(() => {
      const now = Date.now();
      let changed = false;
      cursorMap.current.forEach((c, k) => {
        if (c.ts < now - CURSOR_TTL) {
          cursorMap.current.delete(k);
          changed = true;
        }
      });
      if (changed) setCursors(Array.from(cursorMap.current.values()));
      cameraMap.current.forEach((c, k) => {
        if (c.ts < now - CAMERA_TTL) cameraMap.current.delete(k);
      });
      let selChanged = false;
      selMap.current.forEach((s, k) => {
        if (s.ts < now - SELECTION_TTL) {
          selMap.current.delete(k);
          selChanged = true;
        }
      });
      if (selChanged) rebuildLocks();
    }, 2000);

    // Re-broadcast our selection periodically so a peer that missed a frame (or just connected) still sees
    // our locks, and a dropped update self-heals.
    const heartbeat = window.setInterval(() => {
      const sock = wsRef.current;
      if (!sock || sock.readyState !== WebSocket.OPEN) return;
      const s = hRef.current.self;
      const sel = lastSelection.current;
      sock.send(JSON.stringify({ type: 'selection.sync', name: s.name, color: s.color, selected: sel.selected, editing: sel.editing }));
      // Re-assert our camera so a follower on an otherwise-idle presenter doesn't age out (CAMERA_TTL).
      const cam = lastCamera.current;
      if (cam && !suppressCamera.current) sock.send(JSON.stringify({ type: 'camera.sync', name: s.name, color: s.color, x: cam.x, y: cam.y, zoom: cam.zoom }));
    }, SELECTION_HEARTBEAT);

    return () => {
      closedByUs = true;
      if (timer) window.clearTimeout(timer);
      window.clearInterval(prune);
      window.clearInterval(heartbeat);
      cursorMap.current.clear();
      cameraMap.current.clear();
      selMap.current.clear();
      setCursors([]);
      setLocks(new Map());
      setPresence([]);
      setConnected(false);
      wsRef.current = null;
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteboardId]);

  /** Broadcast this tab's live pointer (world coords), or null when it leaves the canvas. Throttled. */
  const sendCursor = useCallback((world: { x: number; y: number } | null) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (world && now - lastSent.current < CURSOR_SEND_INTERVAL) return;
    lastSent.current = now;
    const s = hRef.current.self;
    ws.send(
      JSON.stringify({
        type: 'cursor.sync',
        name: s.name,
        color: s.color,
        x: world ? world.x : null,
        y: world ? world.y : null,
      }),
    );
  }, []);

  /** Broadcast the elements THIS tab has selected + the one it's editing, so peers lock them. */
  const sendSelection = useCallback((selected: string[], editing: string | null) => {
    lastSelection.current = { selected, editing };
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const s = hRef.current.self;
    ws.send(JSON.stringify({ type: 'selection.sync', name: s.name, color: s.color, selected, editing }));
  }, []);

  /** Broadcast THIS tab's camera (pan + zoom) for followers. Throttled; also cached for the heartbeat. */
  const sendCamera = useCallback((cam: Camera) => {
    lastCamera.current = cam;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastCamSent.current < CAMERA_SEND_INTERVAL) return;
    lastCamSent.current = now;
    const s = hRef.current.self;
    ws.send(JSON.stringify({ type: 'camera.sync', name: s.name, color: s.color, x: cam.x, y: cam.y, zoom: cam.zoom }));
  }, []);

  /** Ask a peer (memberId) or everyone ("all") to jump to + follow OUR current view. */
  const sendBring = useCallback((target: string, cam: Camera) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const s = hRef.current.self;
    ws.send(JSON.stringify({ type: 'bring.here', name: s.name, color: s.color, x: cam.x, y: cam.y, zoom: cam.zoom, target }));
  }, []);

  /** Latest known camera of a peer (by member id), or undefined — read by the follow loop each frame. */
  const getPeerCamera = useCallback((memberId: string): RemoteCamera | undefined => cameraMap.current.get(memberId), []);

  /** Pause/resume broadcasting OUR camera heartbeat (paused while we're following someone else). */
  const setCameraSuppressed = useCallback((v: boolean) => { suppressCamera.current = v; }, []);

  return { presence, cursors, connected, sendCursor, locks, sendSelection, sendCamera, sendBring, getPeerCamera, setCameraSuppressed };
}
